import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Shield, RefreshCw, Search, User, FileText } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Layout from "../components/Layout";
import api from "../lib/api";

const ACTION_STYLES = [
  { re: /override|delete|suspend|error/i, cls: "bg-rose-100 text-rose-800 border-rose-200" },
  { re: /create|add|import|invite/i, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { re: /update|edit|role/i, cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { re: /share|portal|referral|sent|whatsapp/i, cls: "bg-sky-100 text-sky-800 border-sky-200" },
];
const actionCls = (a) => (ACTION_STYLES.find((s) => s.re.test(a)) || {}).cls || "bg-slate-100 text-slate-700 border-slate-200";

// Full activity trail — every create/update/share/override recorded by the
// backend audit() helper, filterable by text, action type and patient.
export default function AuditLog() {
  const [params] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("all");
  const patientId = params.get("patient_id") || "";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/audit${patientId ? `?patient_id=${encodeURIComponent(patientId)}` : ""}`);
      setLogs(r.data.logs || []);
    } catch (e) {
      console.debug("[silent]", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const actions = useMemo(() => [...new Set(logs.map((l) => l.action).filter(Boolean))].sort(), [logs]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return logs.filter((l) => {
      if (action !== "all" && l.action !== action) return false;
      if (!needle) return true;
      const blob = `${l.action} ${l.user_email} ${l.patient_id || ""} ${l.record_id || ""} ${JSON.stringify(l.details || {})}`.toLowerCase();
      return blob.includes(needle);
    });
  }, [logs, q, action]);

  const fmtTs = (s) => {
    if (!s) return "—";
    const d = new Date(s);
    return `${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
  };

  const detailStr = (d) => {
    if (!d || Object.keys(d).length === 0) return "";
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
      .join(" · ");
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="audit-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Shield size={20} className="text-pink-600" /> Audit Trail
            </h1>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Last 200 recorded actions{patientId ? <> for patient <span className="font-semibold">{patientId}</span> — <Link to="/audit" className="text-pink-700 underline">show all</Link></> : " across the clinic"}.
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="gap-1.5" data-testid="audit-refresh-btn">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search user, action, patient, details…"
              className="pl-8 text-sm"
              data-testid="audit-search-input"
            />
          </div>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700"
            data-testid="audit-action-filter"
          >
            <option value="all">All actions ({logs.length})</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <div className="text-[11px] text-slate-400">{rows.length} shown</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-400 text-sm">Loading audit trail…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm" data-testid="audit-empty">No audit entries match.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">When</th>
                    <th className="px-3 py-2 font-semibold">User</th>
                    <th className="px-3 py-2 font-semibold">Action</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">Patient</th>
                    <th className="px-3 py-2 font-semibold">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((l) => (
                    <tr key={l.audit_id} className="hover:bg-slate-50/70 align-top" data-testid={`audit-row-${l.audit_id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600 tabular">{fmtTs(l.timestamp)}</td>
                      <td className="px-3 py-2 text-slate-700">
                        <span className="inline-flex items-center gap-1"><User size={11} className="text-slate-400" /> {l.user_email || l.user_id || "—"}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-semibold ${actionCls(l.action || "")}`}>
                          {l.action}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {l.patient_id ? (
                          <Link to={`/patient/${l.patient_id}`} className="text-pink-700 hover:underline font-medium" data-testid={`audit-patient-link-${l.audit_id}`}>
                            {l.patient_id}
                          </Link>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500 max-w-[380px]">
                        <div className="truncate" title={detailStr(l.details)}>
                          {l.record_id && <span className="inline-flex items-center gap-1 mr-2 text-slate-600"><FileText size={11} /> {l.record_type ? `${l.record_type}:` : ""}{l.record_id}</span>}
                          {detailStr(l.details) || (!l.record_id && <span className="text-slate-300">—</span>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
